<script lang="ts">
	import { devices_status } from 'src/stores/device-runtime/status';
	//Svelte animation
	import { fade } from 'svelte/transition';
	//Svelte hooks
	import { beforeUpdate, onMount } from 'svelte';
	//Helpers
	import {
		get_local_ip,
		real_id,
		get_status_settings_component_path,
		is_gen_2,
		get_gen_2_component_from_category,
		get_device_component_index,
	} from '../../../helpers/interface_helpers';
	import { requestUserColors } from '../../../helpers/colors_helpers';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	// import { colorLimited } from 'src/helpers/utilities';
	//Services
	import { postReq, localReq } from '../../../services/http';
	import { hapticFeedback } from '../../../services/native_container_requests_ios';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { gen1CloudReq } from 'src/services/gen_1_reqs';
	//Stores
	import { devices } from 'src/stores/lists/devices';
	import { os } from 'src/stores/session/native-bridge';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Design system components
	import { ColorWheel, CardGrid, FavColor, Loader } from '@Shelly/shelly-design-system';
	//Components
	import PinUnlock from '../../../components/PinUnlock.svelte';
	import ControlColorSelected from '../Group/ControlColorSelected.svelte';
	// import ControlEffects from './ControlEffects.svelte';
	// import ControlTemp from './ControlTemp.svelte';
	//Constants
	import { PROFILE_DEVICES } from 'src/constants';
	import { cloud_disabled_devices } from 'src/stores/device-runtime/firmware-ui';
	import { format } from 'svelte-i18n';

	export let device,
		status,
		disabled = false;

	const id = real_id(device.id);
	const _comp_path = get_status_settings_component_path(device) || '';
	const isProfiled = PROFILE_DEVICES.includes(device.type);
	const isGen2 = is_gen_2(device);

	let localIp = false;

	let pinLocked;
	let showModal = false;

	let callbackValue;

	let value = { r: 255, g: 0, b: 0 };
	let loading = false;
	let cachedValue = value;
	let loadingFavorites = false;

	let favoriteColors = [];

	$: favColors1Mapped = favoriteColors.map((c) => {
		const currentColor = `rgb(${c.r}, ${c.g}, ${c.b})`;
		const isActive = `rgb(${value.r}, ${value.g}, ${value.b})` === currentColor;

		return {
			active: isActive,
			color: currentColor,
			colorObj: c,
		};
	});

	onMount(async () => {
		valueFromStatus();
		loadingFavorites = true;
		const favColorsReq = await requestUserColors();
		favoriteColors = favColorsReq ? favColorsReq : [];
		loadingFavorites = false;
	});

	beforeUpdate(() => {
		localIp = get_local_ip(id);

		pinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;

		/**
		 * Don't pull color from status while a user-initiated change is in flight,
		 * otherwise stale status (typical on Gen 1 where the device pushes a
		 * pre-command status over WS during the request, but also possible on
		 * Gen 2++) clobbers the optimistic `value` and snaps the color wheel
		 * marker back to the previous color (BUG FE-1633: drag/click on wheel
		 * didn't move the indicator and clicking a fav swatch didn't pick it).
		 *
		 * Mirrors the safe pattern used in ControlGroupColor.svelte.
		 */
		if (!loading) {
			const statusValue = readValueFromStatus();
			if (statusValue && JSON.stringify(value) !== JSON.stringify(statusValue)) {
				value = statusValue;
				cachedValue = statusValue;
			}
		}
	});

	const readValueFromStatus = (): { r: number; g: number; b: number } | undefined => {
		if (device.category === 'sensor' || typeof status?.[_comp_path] === 'undefined') {
			return undefined;
		}
		if (!isProfiled && !isGen2) {
			const channelStatus = status[_comp_path][device.channel];
			if (!channelStatus) return undefined;
			return {
				r: channelStatus.red,
				g: channelStatus.green,
				b: channelStatus.blue,
			};
		}
		if (status[_comp_path].rgb && Array.isArray(status[_comp_path].rgb)) {
			return {
				r: status[_comp_path].rgb[0],
				g: status[_comp_path].rgb[1],
				b: status[_comp_path].rgb[2],
			};
		}
		return undefined;
	};

	const valueFromStatus = () => {
		const statusValue = readValueFromStatus();
		if (statusValue) {
			value = statusValue;
			cachedValue = statusValue;
		}
	};

	const validate = (_value) => {
		const max = 255;
		const min = 0;

		if (+_value > max) {
			_value = max;
		}
		if (+_value < min) {
			_value = min;
		}
		return +_value;
	};

	const handleSelectColor = async (selectedColor) => {
		const validatedColor = {
			r: validate(selectedColor.r),
			g: validate(selectedColor.g),
			b: validate(selectedColor.b),
		};

		// Device is PIN-locked: stash the requested color, optimistically
		// reflect it in `value` so the wheel marker (and fav highlight) jumps
		// to the picked spot, and prompt for the PIN. We keep `loading=true`
		// so the `beforeUpdate` resync guard above doesn't immediately revert
		// `value` from the unchanged status. On dismiss we explicitly revert
		// via `handlePinDismiss`; on successful unlock the regular request
		// path takes over and applies the change for real.
		if (pinLocked) {
			callbackValue = validatedColor;
			loading = true;
			value = validatedColor;
			showModal = true;
			return;
		}

		loading = true;
		try {
			value = validatedColor;
			if ($os === 'IOS') {
				//i think here is good to have haptic only on color change, user had enough color feedback during movement
				hapticFeedback('buttonPressed');
			}
			let res;

			if (!isProfiled && !isGen2) {
				cachedValue = {
					r: status[_comp_path][device.channel].red,
					g: status[_comp_path][device.channel].green,
					b: status[_comp_path][device.channel].blue,
				};

				status[_comp_path][device.channel].red = value.r;
				status[_comp_path][device.channel].green = value.g;
				status[_comp_path][device.channel].blue = value.b;
			} else {
				cachedValue = {
					r: status[_comp_path].rgb[0],
					g: status[_comp_path].rgb[1],
					b: status[_comp_path].rgb[2],
				};

				status[_comp_path].rgb[0] = value.r;
				status[_comp_path].rgb[1] = value.g;
				status[_comp_path].rgb[2] = value.b;
			}
			devices_status.update_device_status({
				id,
				msg: status,
			});

			const colorData = {
				red: value.r,
				green: value.g,
				blue: value.b,
			};

			if (!isGen2) {
				if (localIp) {
					const url = `${localIp}/${status[_comp_path][device.channel].mode}/${device.channel}`;

					res = await localReq(device, url, 'post', colorData, 9000, true);

					if (
						!res.isok &&
						typeof $cloud_disabled_devices[device.id] === 'undefined' // is not cloud disabled
						// $auth_locked_devices.indexOf[real_id(device.id)] < 0 // is not in the auth locked list
					) {
						console.log('Local control failed. Will try cloud request.');
						res = await gen1CloudReq(device, device.category, {
							...colorData,
						});
					}
				} else {
					res = await gen1CloudReq(device, device.category, {
						...colorData,
					});
				}
			} else {
				const dataToRequest = [+value.r.toFixed(0), +value.g.toFixed(0), +value.b.toFixed(0)]; //{...colorData}seens not needed since https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/merge_requests/3228
				res = await gen2Request(device, `${get_gen_2_component_from_category(device)}.set`, {
					id: get_device_component_index(device),
					rgb: dataToRequest,
				});
			}

			if (res.isok) {
				cachedValue = value;
			} else {
				if (!isProfiled && !isGen2) {
					status[_comp_path][device.channel] = {
						...status[_comp_path][device.channel],
						red: cachedValue.r,
						green: cachedValue.g,
						blue: cachedValue.b,
					};
				} else {
					status[_comp_path] = {
						...status[_comp_path],
						red: cachedValue.r,
						green: cachedValue.g,
						blue: cachedValue.b,
					};
				}
				value = cachedValue;
				devices_status.update_device_status({
					id,
					msg: status,
				});
				throw new Error('Failed to set color');
			}
		} catch (error) {
			console.error('Failed to set color', error);
		} finally {
			loading = false;
		}
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		const pendingColor = callbackValue;
		callbackValue = undefined;
		if (pendingColor) {
			// `loading` is still `true` from the pin-locked branch; the
			// non-pin-locked path will set it again (idempotent) and clear it
			// in `finally` once the request resolves.
			handleSelectColor(pendingColor);
		} else {
			loading = false;
		}
	};

	/**
	 * Triggered when the user dismisses the PIN modal (X button or biometry
	 * cancel). Revert the optimistic `value` back to status so the wheel
	 * marker returns to the previous color, discard the pending change, and
	 * clear `loading` so the resync guard re-engages.
	 */
	const handlePinDismiss = () => {
		callbackValue = undefined;
		showModal = false;
		loading = false;
		valueFromStatus();
	};

	//#region Saved colors
	const onSaveColor = (newColor) => {
		if (disabled) return;
		favColors1Mapped = [
			...favColors1Mapped,
			{
				active: true,
				color: `rgb(${newColor.r}, ${newColor.g}, ${newColor.b})`,
				colorObj: newColor,
			},
		];
		updateColorsList();
	};

	const onDeleteColor = (index: number) => {
		favColors1Mapped = favColors1Mapped.filter((_, i) => i !== index);
		updateColorsList();
	};

	const updateColorsList = async () => {
		favoriteColors = favColors1Mapped.map((fc) => {
			return {
				...fc.colorObj,
			};
		});

		const data = {
			colors: favoriteColors,
		};

		const res = await postReq('/interface/user_colors/save', {
			data: JSON.stringify(data),
		});

		if (!res.isok) console.error('Failed to set colors');
	};

	//#endregion Saved colors
</script>

<div in:fade|global={{ duration: 300 }} class={$showNewLayout ? 'pt-s-plus' : ''}>
	<div class:disabled>
		<ColorWheel
			id="colorwheel-ajqz1j2"
			class="mb-s"
			valuesBackgroundColor="--color-0"
			a11yProps={{
				saveButtonAriaLabel: $format('label_save_color', { default: 'Save color' }),
				colorInputBAriaLabel: $format('label_color_input_b', { default: 'Color input B' }),
				colorInputGAriaLabel: $format('label_color_input_g', { default: 'Color input G' }),
				colorInputRAriaLabel: $format('label_color_input_r', { default: 'Color input R' }),
			}}
			valuesType="input"
			color={value}
			hideSaveBtn
			on:color_change={(e) => {
				e.preventDefault;
				handleSelectColor(e.detail.color);
			}}
			disabled={loading}
			on:save_click={(event) => onSaveColor(event.detail.color)}
		/>
	</div>
	{#if loadingFavorites}
		<Loader class="mb-m" />
	{:else}
		<div class:disabled>
			<ControlColorSelected
				class="mb-m"
				selectedColor={value}
				showSaveButton={!favColors1Mapped.some(
					(fc) => JSON.stringify(fc.colorObj) === JSON.stringify(value),
				)}
				on:save_color={(event) => onSaveColor(event.detail)}
			/>
			<CardGrid sm={90}>
				{#each favColors1Mapped as favColor, i}
					<FavColor
						backgroundColor={$showNewLayout ? '--color-0' : '--color-2'}
						iconBtnVariant={$showNewLayout ? 'danger-secondary' : undefined}
						a11yProps={{
							deleteButtonAriaLabel: $format('label_delete_color', {
								default: 'Delete color',
							}),
							favColorAriaLabel: $format('label_fav_color', { default: 'Favorite color' }),
						}}
						color={favColor.color}
						on:click={() => handleSelectColor(favColor.colorObj)}
						on:delete={() => onDeleteColor(i)}
						active={favColor.active}
					/>
				{/each}
			</CardGrid>
		</div>
	{/if}
</div>

{#if showModal}
	<PinUnlock
		bind:showModal
		on:unlock={handlePin}
		on:close={handlePinDismiss}
		on:cancel={handlePinDismiss}
	/>
{/if}

<style>
	.disabled {
		opacity: 0.5;
		pointer-events: none;
	}
</style>
